<template>
  <div class="ojft" data-testid="organization-job-families-tab">
    <JobFamilyCard
      v-if="selectedFamily"
      :family="cardFamily"
      :loading="detailLoading"
      :error="detailError"
      :levels="familyLevels"
      :levels-loading="levelsLoading"
      :levels-error="levelsError"
      :levels-busy="reorderBusy"
      :suggestion-active="suggestionActive"
      :undo-suggestion-busy="undoSuggestionBusy"
      :level-flags="levelFlags"
      :context-data="contextData"
      :context-files="contextFiles"
      :uploading-context-files="uploadingContextFiles"
      :is-saving-context="isSavingContext"
      @retry="retryLoadDetail"
      @retry-levels="retryLoadLevels"
      @add-level="openAddLevelDialog"
      @edit-level="openEditLevelDialog"
      @delete-level="openDeleteLevelDialog"
      @reorder-levels="onReorderLevels"
      @suggest="openSuggestDialog"
      @undo-suggestions="onUndoSuggestions"
      @dismiss-suggestion="onDismissSuggestion"
      @context-save="onContextSave"
      @context-upload="onContextUpload"
      @context-remove="onContextRemove"
      @chatter-click="onFamilyChatter"
      @edit="onFamilyMenuAction('edit')"
      @activate="onFamilyMenuAction('activate')"
      @retire="onFamilyMenuAction('retire')"
      @reactivate="onFamilyMenuAction('reactivate')"
      @archive="onFamilyMenuAction('archive')"
      @unarchive="onFamilyMenuAction('unarchive')"
      @delete="onFamilyMenuAction('delete')"
      data-testid="job-family-card"
    />

    <JobFamiliesList
      v-else
      :jobFamilies="jobFamilies"
      :loading="loading"
      :filters="listFilters"
      :pagination="pagination"
      :applied-filter-chips="filterChipLabels"
      :is-account-empty="isAccountEmpty"
      @filter-change="onFilterChange"
      @more-filters="showFilterModal = true"
      @remove-filter="onRemoveFilter"
      @add-family="openAddDialog"
      @row-click="openDetail"
      @row-chatter="onRowChatter"
      @row-action="onRowAction"
      @page-change="onPageChange"
    />

    <FilterJobFamiliesModal
      :value="showFilterModal"
      :initial-statuses="filters.statuses"
      :initial-archived="filters.archived"
      @input="showFilterModal = $event"
      @apply="onFiltersApplied"
      @reset="onFiltersReset"
    />

    <AddJobFamilyDialog
      :open="showAddDialog"
      :busy="addBusy"
      :errors="addErrors"
      @update:open="showAddDialog = $event"
      @create="onAddCreate"
      @cancel="closeAddDialog"
    />

    <AddFamilyLevelDialog
      :open="showAddLevelDialog"
      :busy="addLevelBusy"
      :errors="addLevelErrors"
      @save="onAddLevelSave"
      @cancel="closeAddLevelDialog"
    />

    <EditFamilyLevelDialog
      v-if="showEditLevelDialog"
      :open="showEditLevelDialog"
      :level="editingLevel"
      :family-id="selectedFamily && selectedFamily.id"
      :busy="editLevelBusy"
      :errors="editLevelErrors"
      @save="onEditLevelSave"
      @cancel="closeEditLevelDialog"
    />

    <DeleteJobFamilyLevelDialog
      v-if="showDeleteLevelDialog"
      :open="showDeleteLevelDialog"
      :level="deletingLevel"
      :sibling-levels="deleteSiblingLevels"
      :busy="deleteLevelBusy"
      :error="deleteLevelError"
      @confirm="onDeleteLevelConfirm"
      @cancel="closeDeleteLevelDialog"
    />
    <SuggestJobFamilyLevelsDialog
      :open="showSuggestDialog"
      :state="suggestDialogState"
      :busy="suggestBusy"
      :phase="suggestDialogPhase"
      :reasoning="suggestDialogReasoning"
      :stalled="suggestDialogStalled"
      :collapsible="Boolean(suggestDialogPhase) || Boolean(suggestDialogReasoning)"
      @close="closeSuggestDialog"
      @abort="onSuggestAbort"
      @retry="runSuggest"
      @exit="closeSuggestDialog"
    />

    <DeleteJobFamilyDialog
      :show="showDeleteFamilyDialog"
      :name="(deleteFamilyTarget && deleteFamilyTarget.name) || ''"
      @onClose="closeDeleteFamilyDialog"
      @onDelete="confirmDeleteFamily"
    />

    <JobFamilyConfirmDialog
      :show="Boolean(pendingAction)"
      :action="pendingAction || 'activate'"
      :name="(actionTarget && actionTarget.name) || ''"
      :errorMessage="unarchiveError"
      @onClose="closeConfirmDialog"
      @onConfirm="confirmPendingAction"
    />

    <UnarchiveNameConflictDialog
      :show="unarchiveConflictOpen"
      objectType="job_family"
      :currentName="(unarchiveTarget && unarchiveTarget.name) || ''"
      :objectId="(unarchiveTarget && unarchiveTarget.id) || ''"
      :accountId="accountId()"
      :busy="unarchiveBusy"
      :errorMessage="unarchiveError"
      :nameError="unarchiveNameError"
      @confirm="unarchiveFamily.submitRename"
      @close="closeConfirmDialog"
      @clear-error="unarchiveFamily.clearErrors"
    />

    <EditJobFamilyDialog
      :open="showEditFamilyDialog"
      :name="(editFamilyTarget && editFamilyTarget.name) || ''"
      :family-id="editFamilyTarget && editFamilyTarget.id"
      :busy="editFamilyBusy"
      :errors="editFamilyErrors"
      @update:open="showEditFamilyDialog = $event"
      @save="onEditFamilySave"
      @cancel="closeEditFamilyDialog"
    />

    <ChatterDrawer
      :drawer="chatterDrawerOpen"
      iconName="org_icon_jobfamily"
      type="Job Family"
      :typeValue="chatterFamilyName"
      object-type="job_family"
      :object-id="chatterFamilyId"
      :chatterList="chatterListData"
      :totalCheckpointChatter="totalChatterCount"
      :loading="chatterLoading"
      @closeDrawer="onCloseChatterDrawer"
      @requestMoreCheckpointsChatter="onRequestMoreChatter"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";

import JobFamiliesList from "@components/JobFamiliesList/JobFamiliesList.vue";
import { ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME } from "@components/webmcp/navigation/routeConstants";
import FilterJobFamiliesModal from "@components/FilterJobFamiliesModal/FilterJobFamiliesModal.vue";
import AddJobFamilyDialog from "@components/AddJobFamilyDialog/AddJobFamilyDialog.vue";
import AddFamilyLevelDialog from "@components/AddFamilyLevelDialog/AddFamilyLevelDialog.vue";
import EditFamilyLevelDialog from "@components/EditFamilyLevelDialog/EditFamilyLevelDialog.vue";
import DeleteJobFamilyLevelDialog from "@components/DeleteJobFamilyLevelDialog/DeleteJobFamilyLevelDialog.vue";
import JobFamilyCard from "@components/JobFamilyCard/JobFamilyCard.vue";
import SuggestJobFamilyLevelsDialog from "@components/SuggestJobFamilyLevelsDialog/SuggestJobFamilyLevelsDialog.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import DeleteJobFamilyDialog from "@components/DeleteJobFamilyDialog/DeleteJobFamilyDialog.vue";
import JobFamilyConfirmDialog from "@components/JobFamilyConfirmDialog/JobFamilyConfirmDialog.vue";
import UnarchiveNameConflictDialog from "@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue";
import { useUnarchiveOrgObject } from "@/composables/useUnarchiveOrgObject";
import EditJobFamilyDialog from "@components/EditJobFamilyDialog/EditJobFamilyDialog.vue";
import {
  activateJobFamily,
  archiveJobFamily,
  deleteJobFamily,
  reactivateJobFamily,
  retireJobFamily,
  updateJobFamily,
} from "@/api/jobFamilyAPI";
import { getChatterList } from "@/api/chatterAPI";
import { transformChatterData } from "@/utils/chatterUtils";
import { fromApiJobFamily } from "@components/JobFamiliesList/jobFamiliesListRow.js";
import { areDefaultFiltersApplied } from "@components/constants/filterDefaultsRegistry";
import {
  fromApiJobFamilyLevel,
  levelFlagsFromChanges,
} from "@components/JobFamilyLevelsTable/jobFamilyLevelsTableRow.js";
import { useJobFamilyStore } from "@/store/pinia/jobFamily";
import {
  getContextFiles,
  uploadContextFiles,
  deleteContextFile,
} from "@/api/contextFilesAPI";
import {
  DEFAULT_ARCHIVED,
  DEFAULT_STATUSES,
  PAGE_SIZE,
  appliedFilterValues,
  buildListParams,
  chipLabels,
  familyNameFieldErrors,
  levelDeleteErrorCode,
  levelFieldErrors,
  pageCountFromResponse,
  removeChipFromFilters,
} from "./organizationJobFamiliesTab.js";

const emit = defineEmits(["active-count"]);

const JOB_FAMILIES_FILTERS_KEY = "organizationJobFamilies";

const route = useRoute();
const router = useRouter();
const app = getCurrentInstance();
const { proxy } = app || {};
const jobFamilyStore = useJobFamilyStore();

const loading = ref(false);
const jobFamilies = ref([]);
const showFilterModal = ref(false);
const showAddDialog = ref(false);
const addBusy = ref(false);
const addErrors = ref({});
const selectedFamily = ref(null);
const detailLoading = ref(false);
const detailError = ref(null);
const familyLevels = ref([]);
const levelsLoading = ref(false);
const levelsError = ref(null);
const reorderBusy = ref(false);
const contextData = ref(null);
const contextFiles = ref([]);
const uploadingContextFiles = ref([]);
const isSavingContext = ref(false);
const showAddLevelDialog = ref(false);
const addLevelBusy = ref(false);
const addLevelErrors = ref({});
const showEditLevelDialog = ref(false);
const editLevelBusy = ref(false);
const editLevelErrors = ref({});
const editingLevel = ref(null);
const showDeleteLevelDialog = ref(false);
const deleteLevelBusy = ref(false);
const deleteLevelError = ref("");
const deletingLevel = ref(null);
const showSuggestDialog = ref(false);
const suggestDialogState = ref("thinking");
const suggestBusy = ref(false);
// Streamed AI Thinking Standard lifecycle state (ER-12294), fed by the suggest SSE
// callbacks and passed through to SuggestJobFamilyLevelsDialog → IQThinkingBanner.
const suggestDialogPhase = ref("");
const suggestDialogReasoning = ref("");
const suggestDialogStalled = ref(false);
// AbortController for the in-flight Suggest call (ER-11833). The "Abort" action
// cancels the client request through this signal; the seq guard below still drops
// any late result.
const suggestAbortController = ref(null);
// Derived from the Pinia store so the "Undo Suggestions" banner survives in-app
// navigation and back to this family within the same tab (ER-11826). The store is
// in-memory only, so the banner is gone after a tab close/reload.
const suggestionActive = computed(() =>
  Boolean(jobFamilyStore.getSuggestionSnapshot(accountId(), selectedFamily.value?.id))
);
const levelFlags = ref({});
// True while the session-scoped Undo POST is in flight: disables the banner's
// Undo action and blocks a second request from a double click.
const undoSuggestionBusy = ref(false);
// Monotonic guard so an in-flight Suggest that was Aborted/closed is ignored
// when it eventually settles. Declared here (not inside the Suggest flow block)
// because closeDetail()/openDetail() call resetSuggestionState() during setup.
let suggestRequestSeq = 0;

// Job Family chatter drawer (ER-11795) — one shared drawer instance serves both
// the detail header and the list-row entry points.
const chatterDrawerOpen = ref(false);
const chatterListData = ref([]);
const totalChatterCount = ref(0);
const chatterFamilyId = ref(null);
const chatterFamilyName = ref("");
const chatterLoadedPages = ref({});
// True while a chatter page is in flight, so the drawer shows a spinner instead
// of flashing its empty "No Job Family history" state before the data arrives.
const chatterLoading = ref(false);

const pendingAction = ref("");
const actionTarget = ref(null);
const showDeleteFamilyDialog = ref(false);
const deleteFamilyTarget = ref(null);
const showEditFamilyDialog = ref(false);
const editFamilyTarget = ref(null);
const editFamilyBusy = ref(false);
const editFamilyErrors = ref({});

const page = ref(1);
const pageCount = ref(1);
const totalCount = ref(0);
// True only when the latest list request succeeded, so a failed load never reads as an empty account.
const lastLoadSucceeded = ref(false);

const filters = reactive({
  statuses: { ...DEFAULT_STATUSES },
  archived: DEFAULT_ARCHIVED,
  search: "",
  quickStatus: "",
});

const listFilters = computed(() => ({
  status: filters.quickStatus,
  search: filters.search,
}));

const pagination = computed(() => ({
  page: page.value,
  pageCount: pageCount.value,
}));

const filterChipLabels = computed(() => chipLabels(filters));

// ER-12358: zero families under the untouched default filters and no search is an empty
// account (not a filtered-empty result), so the list swaps its toolbar for "Add your first".
const isAccountEmpty = computed(() => {
  if (loading.value || !lastLoadSucceeded.value || jobFamilies.value.length > 0) return false;
  if (filters.search.trim() || filters.quickStatus) return false;
  const values = appliedFilterValues(filters);
  return values.length > 0 && areDefaultFiltersApplied(JOB_FAMILIES_FILTERS_KEY, values);
});

// The Levels tab badge reflects the *actual* level count fetched from the levels
// endpoint, not the (stale after add/delete) `level_count` annotation on the family.
const cardFamily = computed(() => {
  if (!selectedFamily.value) return null;
  return { ...selectedFamily.value, levelCount: familyLevels.value.length };
});

// Delete dialog picker: the other levels in this family only (excluded by id in
// the dialog's own helpers), each shown as `ordinal · name`.
const deleteSiblingLevels = computed(() => {
  const deletingId = deletingLevel.value?.id;
  return familyLevels.value.filter((level) => level.id !== deletingId);
});

// Watch route param to open detail view when navigating to /organization/job-families/:id
watch(
  () => route.params.id,
  (familyId) => {
    if (familyId && !selectedFamily.value) {
      openDetail(familyId);
    } else if (!familyId && selectedFamily.value) {
      closeDetail();
    }
  },
  { immediate: true }
);

function accountId() {
  return localStorage.getItem("currentAccountID");
}

function reportError(error, fallbackMessage) {
  console.error(fallbackMessage, error);
  if (proxy && proxy.$notifications && typeof proxy.$notifications.error === "function") {
    proxy.$notifications.error(fallbackMessage);
  }
}

const unarchiveFamily = useUnarchiveOrgObject("job_family", {
  getAccountId: accountId,
  openConfirm: () => {
    pendingAction.value = "unarchive";
  },
  closeConfirm: () => {
    pendingAction.value = "";
    actionTarget.value = null;
  },
  onUnarchived: async ({ id: familyId, response }) => {
    await syncFamilyAfterMutation(familyId, response && response.data);
    await loadFamilies();
  },
  setLoading: (value) => proxy?.$loader?.setAppLoading?.(value),
});
const {
  target: unarchiveTarget,
  conflictOpen: unarchiveConflictOpen,
  busy: unarchiveBusy,
  errorMessage: unarchiveError,
  nameError: unarchiveNameError,
} = unarchiveFamily;

async function loadFamilies() {
  const id = accountId();
  if (!id) return;
  loading.value = true;
  try {
    const { filters: queryFilters, pagination: queryPagination } = buildListParams(filters, {
      page: page.value,
      pageSize: PAGE_SIZE,
    });
    const data = await jobFamilyStore.fetchList(id, queryFilters, queryPagination);
    const results = (data && data.results) || [];
    jobFamilies.value = results.map(fromApiJobFamily);
    totalCount.value = Number(data && data.count) || results.length;
    pageCount.value = pageCountFromResponse(data, PAGE_SIZE);
    lastLoadSucceeded.value = true;
    emit("active-count", totalCount.value);
  } catch (error) {
    lastLoadSucceeded.value = false;
    reportError(error, "Unable to load job families");
    jobFamilies.value = [];
    totalCount.value = 0;
    pageCount.value = 1;
    emit("active-count", 0);
  } finally {
    loading.value = false;
  }
}

function onFilterChange(next) {
  const search = next && typeof next.search === "string" ? next.search : filters.search;
  const status = next && typeof next.status === "string" ? next.status : filters.quickStatus;
  filters.search = search;
  filters.quickStatus = status;
  page.value = 1;
  loadFamilies();
}

function onFiltersApplied(payload) {
  filters.statuses = { ...DEFAULT_STATUSES, ...((payload && payload.statuses) || {}) };
  filters.archived = Boolean(payload && payload.archived);
  filters.quickStatus = "";
  showFilterModal.value = false;
  page.value = 1;
  loadFamilies();
}

function onFiltersReset() {
  filters.statuses = { ...DEFAULT_STATUSES };
  filters.archived = DEFAULT_ARCHIVED;
}

function onRemoveFilter(chipLabel) {
  const next = removeChipFromFilters(filters, chipLabel);
  filters.statuses = next.statuses;
  filters.archived = next.archived;
  page.value = 1;
  loadFamilies();
}

function onPageChange(nextPage) {
  page.value = nextPage;
  loadFamilies();
}

function openAddDialog() {
  addErrors.value = {};
  showAddDialog.value = true;
}

function closeAddDialog() {
  showAddDialog.value = false;
  addErrors.value = {};
}

async function onAddCreate({ name }) {
  const id = accountId();
  if (!id || !name) return;
  addBusy.value = true;
  addErrors.value = {};
  try {
    const created = await jobFamilyStore.create(id, { name });
    closeAddDialog();
    // Post-save landing (rules §6, resolved 2026-09-21): land on the new family's
    // card, not back on the alphabetically-sorted list where it may not appear on
    // page 1. The detail route watcher above opens the card and fetches its data.
    if (router && created && created.id != null) {
      router
        .push({
          name: ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME,
          params: { id: String(created.id) },
        })
        .catch(() => {});
    }
  } catch (error) {
    const fieldErrors = familyNameFieldErrors(error);
    if (Object.keys(fieldErrors).length) {
      // Field failure: keep the dialog open with the entered value preserved and
      // surface the error inline (toast is reserved for non-field failures).
      addErrors.value = fieldErrors;
    } else {
      reportError(error, "Unable to create job family");
    }
  } finally {
    addBusy.value = false;
  }
}

async function openDetail(familyId) {
  const id = accountId();
  if (!id || familyId == null) return;
  // ER-11794 — the detail route watcher re-enters openDetail after the push
  // below resolves the URL; guard against a duplicate fetch for the same family.
  if (detailLoading.value) return;
  detailLoading.value = true;
  detailError.value = null;
  resetSuggestionState();
  let loaded = false;

  // ER-11794 — keep the detail addressable in the URL so the "Job Families"
  // breadcrumb (and the Families menu item) can navigate back to the list.
  // Previously the detail only lived in `selectedFamily` state while the URL
  // stayed on /organization/job-families, so the breadcrumb's href matched the
  // current path and ExBreadcrumbs skipped the push.
  if (router && String(route.params.id) !== String(familyId)) {
    router
      .push({ name: ORGANIZATION_JOB_FAMILY_DETAIL_ROUTE_NAME, params: { id: String(familyId) } })
      .catch(() => {});
  }

  try {
    const data = await jobFamilyStore.fetchOne(id, familyId);
    selectedFamily.value = fromApiJobFamily(data);
    contextData.value = data;
    loaded = true;
    await loadContextFiles(familyId);
  } catch (error) {
    reportError(error, "Unable to load job family");
    detailError.value = error;
  } finally {
    detailLoading.value = false;
  }
  // The levels list is an independent request: the card renders first and the
  // Levels tab shows its own skeleton/error while this resolves.
  if (loaded) {
    await loadLevels(familyId);
  }
}

async function loadLevels(familyId) {
  const id = accountId();
  if (!id || familyId == null) return;
  levelsLoading.value = true;
  levelsError.value = null;
  try {
    const data = await jobFamilyStore.fetchLevels(id, familyId);
    familyLevels.value = (data || []).map(fromApiJobFamilyLevel);
  } catch (error) {
    familyLevels.value = [];
    levelsError.value = error;
  } finally {
    levelsLoading.value = false;
  }
}

function retryLoadLevels() {
  if (selectedFamily.value?.id) {
    loadLevels(selectedFamily.value.id);
  } else if (route.params.id) {
    loadLevels(route.params.id);
  }
}

function levelById(levelId) {
  return familyLevels.value.find((level) => level.id === levelId) || null;
}

function openAddLevelDialog() {
  addLevelErrors.value = {};
  showAddLevelDialog.value = true;
}

function closeAddLevelDialog() {
  showAddLevelDialog.value = false;
  addLevelErrors.value = {};
}

async function onAddLevelSave(payload) {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (!id || !familyId || !payload) return;
  addLevelBusy.value = true;
  addLevelErrors.value = {};
  try {
    const created = await jobFamilyStore.createLevel(id, familyId, {
      name: payload.name,
      descriptor: payload.descriptor,
    });
    // Append on top of the ladder: the create endpoint assigns max(ordinal)+1,
    // and the table renders the highest ordinal first.
    familyLevels.value = [...familyLevels.value, fromApiJobFamilyLevel(created)];
    closeAddLevelDialog();
  } catch (error) {
    addLevelErrors.value = levelFieldErrors(error);
  } finally {
    addLevelBusy.value = false;
  }
}

function openEditLevelDialog(payload) {
  const level = payload && levelById(payload.levelId);
  if (!level) return;
  // A manual edit counts as reviewing the row: clear its suggestion badge
  // immediately so the review state stays accurate even if the edit is cancelled.
  if (level.id != null && levelFlags.value[level.id]) {
    levelFlags.value = { ...levelFlags.value };
    delete levelFlags.value[level.id];
  }
  editingLevel.value = level;
  editLevelErrors.value = {};
  showEditLevelDialog.value = true;
}

function closeEditLevelDialog() {
  showEditLevelDialog.value = false;
  editingLevel.value = null;
  editLevelErrors.value = {};
}

async function onEditLevelSave(payload) {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (!id || !familyId || !payload) return;
  editLevelBusy.value = true;
  editLevelErrors.value = {};
  try {
    const updated = await jobFamilyStore.updateLevel(id, familyId, payload.id, {
      name: payload.name,
      descriptor: payload.descriptor,
    });
    const mapped = fromApiJobFamilyLevel(updated);
    familyLevels.value = familyLevels.value.map((level) =>
      level.id === mapped.id ? mapped : level
    );
    closeEditLevelDialog();
  } catch (error) {
    editLevelErrors.value = levelFieldErrors(error);
  } finally {
    editLevelBusy.value = false;
  }
}

function openDeleteLevelDialog(payload) {
  const level = payload && levelById(payload.levelId);
  if (!level) return;
  deletingLevel.value = level;
  deleteLevelError.value = "";
  showDeleteLevelDialog.value = true;
}

function closeDeleteLevelDialog() {
  showDeleteLevelDialog.value = false;
  deletingLevel.value = null;
  deleteLevelError.value = "";
}

async function onDeleteLevelConfirm(payload) {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (!id || !familyId || !payload) return;
  deleteLevelBusy.value = true;
  deleteLevelError.value = "";
  try {
    const remaining = await jobFamilyStore.deleteLevel(
      id,
      familyId,
      payload.levelId,
      payload.reassignTo
    );
    // The delete response is the family's remaining levels in their new order —
    // re-render from it rather than refetching.
    familyLevels.value = (remaining || []).map(fromApiJobFamilyLevel);
    closeDeleteLevelDialog();
  } catch (error) {
    deleteLevelError.value = levelDeleteErrorCode(error);
  } finally {
    deleteLevelBusy.value = false;
  }
}

async function onReorderLevels(payload) {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  const orderedLevelIds = payload && payload.orderedLevelIds;
  if (!id || !familyId || !Array.isArray(orderedLevelIds)) return;

  // The table already reordered optimistically on drop; we only PATCH the
  // server order here and reconcile our state with the response.
  reorderBusy.value = true;
  const previous = familyLevels.value;
  try {
    const reordered = await jobFamilyStore.reorderLevels(id, familyId, orderedLevelIds);
    familyLevels.value = (reordered || []).map(fromApiJobFamilyLevel);
  } catch (error) {
    // Roll the table back to the server's (unchanged) order: a new array
    // reference forces the table's levels watcher to re-derive from it.
    familyLevels.value = [...previous];
    reportError(error, "Unable to reorder levels");
  } finally {
    reorderBusy.value = false;
  }
}

/* ── IQ Suggest flow (ER-11793) ── */

function abortSuggestRequest() {
  if (suggestAbortController.value) {
    suggestAbortController.value.abort();
    suggestAbortController.value = null;
  }
}

/** True for a user-initiated abort (axios CanceledError / native AbortError). */
function isAbortError(error) {
  return Boolean(
    error &&
      (error.name === "CanceledError" ||
        error.name === "AbortError" ||
        error.code === "ERR_CANCELED")
  );
}

function resetSuggestionState() {
  suggestRequestSeq += 1;
  abortSuggestRequest();
  showSuggestDialog.value = false;
  suggestDialogState.value = "thinking";
  suggestBusy.value = false;
  suggestDialogPhase.value = "";
  suggestDialogReasoning.value = "";
  suggestDialogStalled.value = false;
  levelFlags.value = {};
  undoSuggestionBusy.value = false;
}

function openSuggestDialog() {
  if (!selectedFamily.value?.id) return;
  suggestRequestSeq += 1;
  showSuggestDialog.value = true;
  suggestDialogState.value = "thinking";
  suggestBusy.value = false;
  suggestDialogPhase.value = "";
  suggestDialogReasoning.value = "";
  suggestDialogStalled.value = false;
  runSuggest();
}

async function runSuggest() {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (!id || familyId == null) {
    suggestDialogState.value = "failed";
    return;
  }
  const requestToken = suggestRequestSeq;
  // Any prior controller (retry after a failure) is already settled; abort it so
  // a fresh controller is unambiguous.
  abortSuggestRequest();
  const controller = new AbortController();
  suggestAbortController.value = controller;
  suggestBusy.value = true;
  suggestDialogState.value = "thinking";
  // ER-12294 — a (re)run starts a fresh, stateless session: reset the streamed
  // lifecycle so the banner returns to Connecting until the first event.
  suggestDialogPhase.value = "";
  suggestDialogReasoning.value = "";
  suggestDialogStalled.value = false;
  try {
    // ER-11826: chain onto any still-pending snapshot so a re-run updates the
    // saved levels in place (and reuses the snapshot id the Undo banner holds).
    const pendingSnapshotId = jobFamilyStore.getSuggestionSnapshot(id, familyId);
    const data = await jobFamilyStore.suggestLevels(
      id,
      familyId,
      controller.signal,
      pendingSnapshotId,
      {
        onPhase: (phase) => {
          if (requestToken !== suggestRequestSeq) return;
          suggestDialogPhase.value = phase;
          // Events resumed after a stall: clear the watchdog label.
          suggestDialogStalled.value = false;
        },
        onThinking: (reasoning) => {
          if (requestToken !== suggestRequestSeq) return;
          suggestDialogReasoning.value = reasoning;
          suggestDialogStalled.value = false;
        },
        onStalled: () => {
          if (requestToken !== suggestRequestSeq) return;
          suggestDialogStalled.value = true;
        },
      }
    );
    if (requestToken !== suggestRequestSeq) return;
    // Suggest saves immediately (auto-save): adopt the reconciled level list,
    // the per-row New/Updated flags and the session-scoped snapshot id.
    familyLevels.value = ((data && data.levels) || []).map(fromApiJobFamilyLevel);
    levelFlags.value = levelFlagsFromChanges(data && data.changes);
    jobFamilyStore.setSuggestionSnapshot(
      id,
      familyId,
      (data && data.snapshot_id) || pendingSnapshotId || null
    );
    showSuggestDialog.value = false;
    suggestDialogState.value = "thinking";
  } catch (error) {
    if (requestToken !== suggestRequestSeq) return;
    // A user-initiated abort is not a failure: no error state/toast.
    if (isAbortError(error)) return;
    suggestDialogState.value = "failed";
  } finally {
    if (requestToken === suggestRequestSeq) {
      suggestBusy.value = false;
      suggestAbortController.value = null;
    }
  }
}

function onSuggestAbort() {
  // Abort the in-flight attempt without applying anything: cancel the HTTP
  // request via the AbortController (ER-11833) and let the seq guard drop any
  // result that still manages to settle.
  suggestRequestSeq += 1;
  abortSuggestRequest();
  showSuggestDialog.value = false;
  suggestDialogState.value = "thinking";
  suggestBusy.value = false;
  suggestDialogPhase.value = "";
  suggestDialogReasoning.value = "";
  suggestDialogStalled.value = false;
}

function closeSuggestDialog() {
  suggestRequestSeq += 1;
  abortSuggestRequest();
  showSuggestDialog.value = false;
  suggestDialogState.value = "thinking";
  suggestBusy.value = false;
  suggestDialogPhase.value = "";
  suggestDialogReasoning.value = "";
  suggestDialogStalled.value = false;
}

async function onUndoSuggestions() {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  const snapshotId = jobFamilyStore.getSuggestionSnapshot(id, familyId);
  if (!id || familyId == null || !snapshotId) return;
  // In-flight guard: Undo is not idempotent-safe to double-fire, and the banner
  // button is disabled off this same flag while the request is pending.
  if (undoSuggestionBusy.value) return;
  undoSuggestionBusy.value = true;
  try {
    const data = await jobFamilyStore.undoSuggestion(id, familyId, snapshotId);
    familyLevels.value = ((data && data.levels) || []).map(fromApiJobFamilyLevel);
  } catch (error) {
    reportError(error, "Unable to undo suggestions");
    return;
  } finally {
    undoSuggestionBusy.value = false;
  }
  // Undo reverts the batch and forfeits the review/undo affordance.
  jobFamilyStore.clearSuggestionSnapshot(id, familyId);
  levelFlags.value = {};
}

function onDismissSuggestion() {
  // Closing the banner ends the review and forfeits Undo — no server call.
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (id && familyId != null) {
    jobFamilyStore.clearSuggestionSnapshot(id, familyId);
  }
  levelFlags.value = {};
}

function closeDetail() {
  selectedFamily.value = null;
  contextData.value = null;
  familyLevels.value = [];
  contextFiles.value = [];
  detailError.value = null;
  levelsLoading.value = false;
  levelsError.value = null;
  reorderBusy.value = false;
  resetSuggestionState();
  closeAddLevelDialog();
  closeEditLevelDialog();
  closeDeleteLevelDialog();
  // Navigate back to list if we're on a detail route
  if (route.params.id) {
    router.push("/organization/job-families");
  }
}

function retryLoadDetail() {
  if (selectedFamily.value?.id) {
    openDetail(selectedFamily.value.id);
  } else if (route.params.id) {
    openDetail(route.params.id);
  }
}

async function loadContextFiles(familyId) {
  const id = accountId();
  if (!id || !familyId) return;
  try {
    const response = await getContextFiles("job_family", familyId);
    contextFiles.value = response.data || [];
  } catch (error) {
    // Context files are optional; don't block on failure
    contextFiles.value = [];
  }
}

async function onContextSave(value) {
  const id = accountId();
  const familyId = selectedFamily.value?.id;
  if (!id || !familyId) return;
  isSavingContext.value = true;
  try {
    // Generic entity PATCH per spec (editRole pattern, not a dedicated endpoint).
    // The call lives in the Pinia action / API module, never in the component.
    await jobFamilyStore.update(id, familyId, { context: value });
    // Re-fetch the family to get updated context_updated_at/by
    await openDetail(familyId);
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      proxy.$notifications.success("Context saved successfully");
    }
  } catch (error) {
    reportError(error, "Unable to save context");
  } finally {
    isSavingContext.value = false;
  }
}

async function onContextUpload(files) {
  const familyId = selectedFamily.value?.id;
  if (!familyId || !files || files.length === 0) return;
  try {
    uploadingContextFiles.value = Array.from(files).map((file) => ({
      name: file.name,
      progress: 0,
    }));
    await uploadContextFiles("job_family", familyId, files, (progressEvent) => {
      // Update progress if needed
    });
    await loadContextFiles(familyId);
    uploadingContextFiles.value = [];
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      proxy.$notifications.success("File uploaded successfully");
    }
  } catch (error) {
    reportError(error, "Unable to upload file");
    uploadingContextFiles.value = [];
  }
}

async function onContextRemove(fileId) {
  const familyId = selectedFamily.value?.id;
  if (!familyId || !fileId) return;
  try {
    await deleteContextFile(fileId);
    await loadContextFiles(familyId);
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      proxy.$notifications.success("File removed successfully");
    }
  } catch (error) {
    reportError(error, "Unable to remove file");
  }
}

/* ── Job Family chatter drawer (ER-11795) ── */

function clearChatterLoadedPages(familyId) {
  if (familyId != null && chatterLoadedPages.value[familyId]) {
    chatterLoadedPages.value[familyId].clear();
  }
}

async function getChatterData(familyId) {
  const id = accountId();
  const targetFamilyId = familyId ?? chatterFamilyId.value;
  if (!id || targetFamilyId == null) return;

  const currentLength = chatterListData.value.length;
  const page = Math.floor(currentLength / 10) + 1;

  if (!chatterLoadedPages.value[targetFamilyId]) {
    chatterLoadedPages.value[targetFamilyId] = new Set();
  }
  if (chatterLoadedPages.value[targetFamilyId].has(page)) return;

  chatterLoading.value = true;
  try {
    const { data } = await getChatterList(id, targetFamilyId, "job_family", page);
    chatterLoadedPages.value[targetFamilyId].add(page);

    if (data && data.count != null) {
      totalChatterCount.value = data.count;
    }

    if (data && Array.isArray(data.results)) {
      chatterListData.value.push(...transformChatterData(data.results));
    }
  } catch (error) {
    console.error("Error fetching job family chatter data:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function reloadChatterData(familyId) {
  if (familyId == null) return;
  chatterListData.value = [];
  totalChatterCount.value = 0;
  clearChatterLoadedPages(familyId);
  await getChatterData(familyId);
}

function openChatterForFamily(familyId, familyName) {
  if (familyId == null) return;
  chatterFamilyId.value = familyId;
  chatterFamilyName.value = familyName || "";
  chatterDrawerOpen.value = true;
  reloadChatterData(familyId);
}

function onFamilyChatter() {
  const family = selectedFamily.value;
  if (!family || family.id == null) return;
  openChatterForFamily(family.id, family.name);
}

function onRowChatter(familyId) {
  if (familyId == null) return;
  const family = jobFamilies.value.find((item) => item.id === familyId);
  openChatterForFamily(familyId, family ? family.name : "");
}

function onCloseChatterDrawer() {
  chatterDrawerOpen.value = false;
}

function onRequestMoreChatter() {
  getChatterData(chatterFamilyId.value);
}

function resolveFamilyTarget(familyId) {
  const fromList = jobFamilies.value.find((row) => row.id === familyId);
  const fromDetail =
    selectedFamily.value && selectedFamily.value.id === familyId ? selectedFamily.value : null;
  return fromList || fromDetail || { id: familyId, name: "" };
}

function onFamilyMenuAction(action) {
  const family = selectedFamily.value;
  if (!family || family.id == null) return;
  onRowAction({ id: family.id, action });
}

function onRowAction({ id: familyId, action }) {
  const id = accountId();
  if (!id || familyId == null || !action) return;

  if (action === "edit") {
    const target = resolveFamilyTarget(familyId);
    editFamilyTarget.value = target;
    editFamilyErrors.value = {};
    showEditFamilyDialog.value = true;
    return;
  }

  const target = resolveFamilyTarget(familyId);
  if (action === "delete") {
    deleteFamilyTarget.value = target;
    showDeleteFamilyDialog.value = true;
    return;
  }

  if (
    action === "activate" ||
    action === "retire" ||
    action === "reactivate" ||
    action === "archive" ||
    action === "unarchive"
  ) {
    actionTarget.value = target;
    if (action === "unarchive") {
      unarchiveFamily.start({ id: target.id, name: target.name || "" });
      return;
    }
    pendingAction.value = action;
  }
}

function closeConfirmDialog() {
  pendingAction.value = "";
  actionTarget.value = null;
  unarchiveFamily.close();
}

function closeDeleteFamilyDialog() {
  showDeleteFamilyDialog.value = false;
  deleteFamilyTarget.value = null;
}

function closeEditFamilyDialog() {
  showEditFamilyDialog.value = false;
  editFamilyTarget.value = null;
  editFamilyErrors.value = {};
}

async function syncFamilyAfterMutation(familyId, payload) {
  if (!payload) return;
  const mapped = fromApiJobFamily(payload);
  if (selectedFamily.value && selectedFamily.value.id === familyId) {
    selectedFamily.value = { ...selectedFamily.value, ...mapped };
  }
}

async function confirmPendingAction() {
  const id = accountId();
  const target = actionTarget.value;
  const action = pendingAction.value;
  if (!id || !target || target.id == null || !action) return;
  if (action === "unarchive") {
    await unarchiveFamily.confirm();
    return;
  }
  try {
    let response;
    if (action === "retire") {
      response = await retireJobFamily(id, target.id);
    } else if (action === "activate") {
      response = await activateJobFamily(id, target.id);
    } else if (action === "reactivate") {
      response = await reactivateJobFamily(id, target.id);
    } else if (action === "archive") {
      response = await archiveJobFamily(id, target.id);
    }
    await syncFamilyAfterMutation(target.id, response && response.data);
    closeConfirmDialog();
    await loadFamilies();
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      const labels = {
        activate: "activated",
        retire: "retired",
        reactivate: "reactivated",
        archive: "archived",
      };
      proxy.$notifications.success(`Job family ${labels[action] || "updated"} successfully`);
    }
  } catch (error) {
    reportError(error, `Unable to ${action} job family`);
  }
}

async function confirmDeleteFamily() {
  const id = accountId();
  const target = deleteFamilyTarget.value;
  if (!id || !target || target.id == null) return;
  try {
    await deleteJobFamily(id, target.id);
    closeDeleteFamilyDialog();
    if (selectedFamily.value && selectedFamily.value.id === target.id) {
      closeDetail();
    }
    await loadFamilies();
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      proxy.$notifications.success("Job family deleted");
    }
  } catch (error) {
    reportError(error, "Unable to delete job family");
  }
}

async function onEditFamilySave({ name }) {
  const id = accountId();
  const target = editFamilyTarget.value;
  if (!id || !target || target.id == null || !name) return;
  editFamilyBusy.value = true;
  editFamilyErrors.value = {};
  try {
    const { data } = await updateJobFamily(id, target.id, { name });
    await syncFamilyAfterMutation(target.id, data);
    closeEditFamilyDialog();
    await loadFamilies();
    if (proxy && proxy.$notifications && typeof proxy.$notifications.success === "function") {
      proxy.$notifications.success("Job family updated");
    }
  } catch (error) {
    editFamilyErrors.value = familyNameFieldErrors(error);
    if (!Object.keys(editFamilyErrors.value).length) {
      reportError(error, "Unable to update job family");
    }
  } finally {
    editFamilyBusy.value = false;
  }
}

onMounted(loadFamilies);
</script>

<style scoped>
.ojft {
  width: 100%;
}
</style>
